import { Stack } from '@chakra-ui/react';
import VAccordionCard from '@voly/ui-components/src/v2/VAccordionCard';
import VFormColumnHorizontal from '@voly/ui-components/src/v2/VFormColumnHorizontal';
import VButton from '@voly/ui-components/src/v2/VButton.jsx';
import VSeparator from '@voly/ui-components/src/v2/VSeparator.jsx';
import VStackRow from '@voly/ui-components/src/v2/VStackRow.jsx';
import VText from '@voly/ui-components/src/v2/VText.jsx';
import { ComponentType, Fragment, ReactElement, useState } from 'react';
import { translate } from 'src/app/project/features/translate/translate';
import {
  TCustomField,
  TWorkspaceCustomFields,
} from 'src/app/project/modules/custom-fields/custom-fields.model';
import { ECustomFieldType } from 'src/app/project/modules/custom-fields/custom-field-types-enums';
import { TPointCustomFieldSubfield } from 'src/app/project/view-models/custom-field-response-model';
import PointFieldNumeric from '../molecules/custom-fields/PointFieldNumeric';
import { EDateFormat } from 'src/app/project/shared/enums/date-format.enum';
import PointFieldNumericSubValues from '../molecules/custom-fields/PointFieldNumericSubValues';
import PointFieldText from '../molecules/custom-fields/PointFieldText';
import PointFieldCheckbox from '../molecules/custom-fields/PointFieldCheckbox';
import PointFieldTime from '../molecules/custom-fields/PointFieldTime';
import PointFieldRichText from '../molecules/custom-fields/PointFieldRichText';
import PointFieldMultiList from '../molecules/custom-fields/PointFieldMultiList';
import PointFieldList from '../molecules/custom-fields/PointFieldList';
import PointFieldDate from '../molecules/custom-fields/PointFieldDate';
import PointFieldFormula from '../molecules/custom-fields/PointFieldFormula';
import { TMentionUser } from '@voly/ui-components/src/v2/VRichText/VRichTextMention';
import { TAllUsers } from '@project/view-models';
import { setUserList } from 'src/app/project/components/input/rich-text/utils/set-user-list';

export type TPointFieldEntry = {
  field: TCustomField;
  value: string;
  subValues?: TPointCustomFieldSubfield[];
  canEdit: boolean;
  onSave: (value: string, mentionIds?: string[]) => void;
  onSaveSubValues?: (subValues: TPointCustomFieldSubfield[]) => void;
};

export type TPointFieldIdsEntry = {
  field: TCustomField;
  selectedIds: string[];
  canEdit: boolean;
  onSave: (selectedIds: string[]) => void;
};

export type TPointFieldRowEntry = TPointFieldEntry | TPointFieldIdsEntry;

export type TPointFieldGroup = {
  id: string;
  title: string;
  fields: TPointFieldRowEntry[];
};

export type TPointFieldControlProps = {
  field: TCustomField;
  value: string;
  subValues?: TPointCustomFieldSubfield[];
  readOnly: boolean;
  dateFormat: EDateFormat;
  onSave: (value: string, mentionIds?: string[]) => void;
  onSaveSubValues?: (subValues: TPointCustomFieldSubfield[]) => void;
  mentions?: TMentionUser[];
  customFields?: TWorkspaceCustomFields;
};

type TPointFieldControl = ComponentType<TPointFieldControlProps>;

export const pointFieldControls: Record<ECustomFieldType, TPointFieldControl | null> = {
  [ECustomFieldType.TEXT]: PointFieldText,
  [ECustomFieldType.LIST]: PointFieldList,
  [ECustomFieldType.COST]: PointFieldNumeric,
  [ECustomFieldType.NUMBERS]: PointFieldNumeric,
  [ECustomFieldType.DATE]: PointFieldDate,
  [ECustomFieldType.RICHTEXT]: PointFieldRichText,
  [ECustomFieldType.TIME]: PointFieldTime,
  [ECustomFieldType.PERCENTAGE]: PointFieldNumeric,
  [ECustomFieldType.CHECKBOX]: PointFieldCheckbox,
  [ECustomFieldType.TIMELINE]: PointFieldDate,
  [ECustomFieldType.FORMULA]: PointFieldFormula,
  [ECustomFieldType.MULTI_LIST]: null,
  [ECustomFieldType.SIGNATURE]: null,
};

const resolveControl = (field: TCustomField): TPointFieldControl | null => {
  const Control = pointFieldControls[field.type];
  if (Control === PointFieldNumeric && field.subValuesActive) {
    return PointFieldNumericSubValues;
  }
  return Control;
};

export type TPointFieldLayoutEntry = TPointFieldRowEntry | TPointFieldGroup;

export const isPointFieldGroup = (entry: TPointFieldLayoutEntry): entry is TPointFieldGroup =>
  'fields' in entry;

export const isPointFieldIdsEntry = (entry: TPointFieldRowEntry): entry is TPointFieldIdsEntry =>
  'selectedIds' in entry;

interface PointFieldsProps {
  fields: TPointFieldLayoutEntry[];
  users: TAllUsers;
  dateFormat: EDateFormat;
}

const toCustomFields = (fields: TPointFieldLayoutEntry[]): TWorkspaceCustomFields =>
  Object.fromEntries(
    fields
      .flatMap((entry) => (isPointFieldGroup(entry) ? entry.fields : [entry]))
      .map((entry) => [entry.field.id, entry.field]),
  );

const renderRow = (
  entry: TPointFieldRowEntry,
  dateFormat: EDateFormat,
  mentions: TMentionUser[],
  customFields: TWorkspaceCustomFields,
): ReactElement | null => {
  if (isPointFieldIdsEntry(entry)) {
    return (
      <PointFieldMultiList
        value={entry.selectedIds}
        readOnly={!entry.canEdit}
        field={entry.field}
        onSave={entry.onSave}
      />
    );
  }

  const Control = resolveControl(entry.field);

  if (!Control) {
    return null;
  }

  return (
    <Control
      value={entry.value}
      subValues={entry.subValues}
      readOnly={!entry.canEdit}
      field={entry.field}
      dateFormat={dateFormat}
      onSave={entry.onSave}
      onSaveSubValues={entry.onSaveSubValues}
      mentions={mentions}
      customFields={customFields}
    />
  );
};

type TFieldGroupsHeadingProps = {
  isAnyGroupOpen: boolean;
  onToggleAll: () => void;
};

const FieldGroupsHeading = ({
  isAnyGroupOpen,
  onToggleAll,
}: TFieldGroupsHeadingProps): ReactElement => (
  <VStackRow gap={1}>
    <VText textStyle="sm" fontWeight="semibold" color="fg.subtle">
      {translate('custom_fields')}
    </VText>
    <VSeparator flex="1" />
    <VButton color="tertiary" size="small" onClick={onToggleAll}>
      {translate(isAnyGroupOpen ? 'collapse_all' : 'expand_all')}
    </VButton>
  </VStackRow>
);

const PointFields = ({ fields, users, dateFormat }: PointFieldsProps): ReactElement => {
  const customFields = toCustomFields(fields);
  const mentions = setUserList(users, Object.keys(users));
  const groupIds = fields.filter(isPointFieldGroup).map((group) => group.id);
  const [closedGroupIds, setClosedGroupIds] = useState<string[]>([]);
  const isAnyGroupOpen = groupIds.some((id) => !closedGroupIds.includes(id));

  const toggleAll = (): void => {
    setClosedGroupIds(isAnyGroupOpen ? groupIds : []);
  };

  const setGroupOpen = (groupId: string, open: boolean): void => {
    const otherClosedIds = closedGroupIds.filter((id) => id !== groupId);
    setClosedGroupIds(open ? otherClosedIds : [...otherClosedIds, groupId]);
  };

  return (
    <VFormColumnHorizontal gap={4} data-testid="point-fields">
      {groupIds.length > 0 && (
        <FieldGroupsHeading isAnyGroupOpen={isAnyGroupOpen} onToggleAll={toggleAll} />
      )}
      {fields.map((entry) =>
        isPointFieldGroup(entry) ? (
          <VAccordionCard
            key={entry.id}
            title={entry.title}
            value={entry.id}
            open={!closedGroupIds.includes(entry.id)}
            onOpenChange={(open) => setGroupOpen(entry.id, open)}
            header="plain"
            indicatorPosition="end"
            truncateTitle
          >
            <Stack gap={5}>
              {entry.fields.map((groupedEntry) => (
                <Fragment key={groupedEntry.field.id}>
                  {renderRow(groupedEntry, dateFormat, mentions, customFields)}
                </Fragment>
              ))}
            </Stack>
          </VAccordionCard>
        ) : (
          <Fragment key={entry.field.id}>
            {renderRow(entry, dateFormat, mentions, customFields)}
          </Fragment>
        ),
      )}
    </VFormColumnHorizontal>
  );
};

export default PointFields;
